/**
 * 2026-04-25 — GM·디버그용 치트 패널. 히든.
 *
 * 진입점: 어디서든 "qqwweerr" 타이핑 → 열림 (3초 이내 순서대로 입력).
 * 닫기: ESC 또는 우상단 X.
 *
 * 제공 기능:
 *   - 골드 직접 세팅
 *   - 아이템 id 검색 → 수량 지정 → 인벤토리 지급
 *   - 프리셋 재료 번들 (refine / engrave / all) 지급
 *   - 훈련 진행도 (pathId → 누적) 세팅
 *   - 장비 instanceId 강화 단계 세팅
 *
 * runId 는 useRun 에서 읽어옴. 런이 없으면 대부분 기능 비활성.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { PrimaryButton } from "@/components/Button/PrimaryButton";
import {
    cheatCatalog, cheatSetGold, cheatGrantItem, cheatBundle,
    cheatSetTraining, cheatSetEnhancement,
    type CheatCatalogEntry, type CheatBundle,
} from "@/api/endpoints/cheat";
import { getCharacterSheet } from "@/api/endpoints/inventory";
import { useRun } from "@/state/run";

// ─── 히든 트리거 (qqwweerr 타이핑) ─────────────────────────────────────

const TRIGGER = "qqwweerr";

export function useCheatTrigger(onOpen: () => void) {
    const bufferRef = useRef<string>("");
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            // 입력 필드 내에선 무시
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
            if (e.key.length !== 1) return;
            bufferRef.current = (bufferRef.current + e.key.toLowerCase()).slice(-TRIGGER.length);
            if (bufferRef.current === TRIGGER) {
                bufferRef.current = "";
                onOpen();
                return;
            }
            // 3초 침묵 시 버퍼 초기화
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(() => { bufferRef.current = ""; }, 3000);
        };
        window.addEventListener("keydown", handler);
        return () => {
            window.removeEventListener("keydown", handler);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [onOpen]);
}

// ─── CheatGate (글로벌 진입점) ────────────────────────────────────────

/**
 * App 최상위에 한 번만 마운트. 언제든 idkfa 타이핑하면 패널 열림.
 */
export function CheatGate() {
    const [open, setOpen] = useState(false);
    useCheatTrigger(() => setOpen(true));
    if (!open) return null;
    return createPortal(<CheatPanel onClose={() => setOpen(false)} />, document.body);
}

// ─── CheatPanel ──────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
}

type Tab = "gold" | "item" | "bundle" | "training" | "enhance";

function CheatPanel({ onClose }: Props) {
    const runId = useRun(s => s.runId);
    const setSheet = useRun(s => s.setSheet);
    const [tab, setTab] = useState<Tab>("gold");
    const [log, setLog] = useState<string[]>([]);

    // ESC 닫기
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const push = (msg: string) => setLog(prev => [...prev, `${new Date().toTimeString().slice(0, 8)} · ${msg}`].slice(-20));

    // 2026-04-25: 치트 실행 후 인벤토리·스탯·런이 서버 쪽에서 갱신되었지만 프론트 store 는 stale.
    //   refresh() 를 각 탭에 내려주고 grant/bundle/setEnhancement 직후 호출해 store 새로고침.
    //   이걸 안 하면 유저가 "재료 분명히 넣었는데 RefineModal 에서 재료 부족" 을 보게 됨.
    const refresh = async () => {
        if (!runId) return;
        try {
            const sheet = await getCharacterSheet(runId);
            setSheet(sheet);
        } catch (e: unknown) {
            push(`새로고침 실패: ${(e as Error).message ?? e}`);
        }
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <motion.div
                onClick={e => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={modalStyle}
            >
                <div style={headerStyle}>
                    <div>
                        <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 2, color: "#FF6B6B" }}>
                            ⚠ DEV / GM PANEL ⚠
                        </div>
                        <h2 style={{ fontSize: 20, margin: "4px 0 0", color: tokens.color.brass }}>
                            치트 콘솔
                        </h2>
                        <p style={{ fontSize: 11, opacity: 0.7, margin: "4px 0 0" }}>
                            현재 런: <code style={codeStyle}>{runId ?? "(없음)"}</code>
                        </p>
                    </div>
                    <button onClick={onClose} style={closeBtn}>✕</button>
                </div>

                <div style={tabRow}>
                    {(["gold", "item", "bundle", "training", "enhance"] as Tab[]).map(t => (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            style={{
                                ...tabBtn,
                                background: tab === t ? `${tokens.color.brass}22` : "transparent",
                                color: tab === t ? tokens.color.brass : tokens.color.parchment,
                                borderColor: tab === t ? tokens.color.brass : `${tokens.color.brass}33`,
                            }}
                        >
                            {tabLabel(t)}
                        </button>
                    ))}
                </div>

                <div style={bodyStyle}>
                    {!runId && tab !== "gold" && (
                        <p style={{ color: "#FF9B9B", fontSize: 12, fontStyle: "italic" }}>
                            런이 활성화되어야 사용 가능합니다.
                        </p>
                    )}
                    {tab === "gold" && <GoldTab runId={runId} push={push} refresh={refresh} />}
                    {tab === "item" && runId && <ItemTab runId={runId} push={push} refresh={refresh} />}
                    {tab === "bundle" && runId && <BundleTab runId={runId} push={push} refresh={refresh} />}
                    {tab === "training" && runId && <TrainingTab runId={runId} push={push} refresh={refresh} />}
                    {tab === "enhance" && runId && <EnhanceTab runId={runId} push={push} refresh={refresh} />}
                </div>

                {log.length > 0 && (
                    <div style={logStyle}>
                        {log.slice().reverse().map((msg, i) => (
                            <div key={i} style={logLine}>{msg}</div>
                        ))}
                    </div>
                )}
            </motion.div>
        </div>
    );
}

function tabLabel(t: Tab): string {
    switch (t) {
        case "gold": return "💰 골드";
        case "item": return "📦 아이템";
        case "bundle": return "🎁 번들";
        case "training": return "📘 훈련";
        case "enhance": return "🔨 제련 세팅";
    }
}

// ─── Tabs ────────────────────────────────────────────────────────────

function GoldTab({ runId, push, refresh }: { runId: string | null | undefined; push: (s: string) => void; refresh: () => Promise<void> }) {
    const [value, setValue] = useState(99999);
    const submit = async () => {
        if (!runId) return;
        try {
            const newGold = await cheatSetGold(runId, value);
            push(`골드 세팅 완료 → ${newGold}`);
            await refresh();
        } catch (e: any) {
            push(`실패: ${e.message ?? e}`);
        }
    };
    return (
        <div style={tabInnerStyle}>
            <label style={labelStyle}>세팅할 골드 값</label>
            <input
                type="number"
                value={value}
                onChange={e => setValue(Number(e.target.value))}
                style={inputStyle}
            />
            <div style={quickRow}>
                {[0, 1000, 10000, 99999, 999999].map(v => (
                    <button key={v} onClick={() => setValue(v)} style={quickBtn}>{v.toLocaleString()}</button>
                ))}
            </div>
            <PrimaryButton onClick={submit} disabled={!runId}>적용</PrimaryButton>
        </div>
    );
}

function ItemTab({ runId, push, refresh }: { runId: string; push: (s: string) => void; refresh: () => Promise<void> }) {
    const [catalog, setCatalog] = useState<CheatCatalogEntry[]>([]);
    const [query, setQuery] = useState("");
    const [itemId, setItemId] = useState("");
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        cheatCatalog().then(setCatalog).catch(e => push(`카탈로그 로드 실패: ${e.message ?? e}`));
    }, []);

    const filtered = useMemo(() => {
        if (!query.trim()) return catalog.slice(0, 20);
        const q = query.toLowerCase();
        return catalog.filter(e =>
            e.id.toLowerCase().includes(q) || e.name.toLowerCase().includes(q) || e.category.toLowerCase().includes(q)
        ).slice(0, 30);
    }, [catalog, query]);

    const submit = async () => {
        if (!itemId) return;
        try {
            await cheatGrantItem(runId, itemId, quantity);
            push(`지급: ${itemId} × ${quantity}`);
            await refresh();
        } catch (e: any) {
            push(`실패: ${e.message ?? e}`);
        }
    };

    return (
        <div style={tabInnerStyle}>
            <label style={labelStyle}>검색 (id · 이름 · 카테고리)</label>
            <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="예: refine_stone, 장검, WEAPON..."
                style={inputStyle}
            />
            <div style={listStyle}>
                {filtered.map(e => (
                    <button
                        key={e.id}
                        onClick={() => setItemId(e.id)}
                        style={{
                            ...listRow,
                            background: itemId === e.id ? `${tokens.color.brass}22` : "transparent",
                        }}
                    >
                        <span style={{ flex: 1, fontSize: 12 }}><strong>{e.name}</strong> <span style={{ opacity: 0.5 }}>({e.id})</span></span>
                        <span style={{ fontSize: 10, opacity: 0.6 }}>{e.category}</span>
                    </button>
                ))}
                {filtered.length === 0 && <div style={{ opacity: 0.5, fontSize: 11, padding: 12 }}>검색 결과 없음</div>}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                <label style={{ ...labelStyle, margin: 0 }}>수량</label>
                <input
                    type="number"
                    value={quantity}
                    onChange={e => setQuantity(Number(e.target.value))}
                    min={1}
                    style={{ ...inputStyle, width: 80 }}
                />
                <code style={codeStyle}>{itemId || "(선택 필요)"}</code>
            </div>
            <PrimaryButton onClick={submit} disabled={!itemId}>지급</PrimaryButton>
        </div>
    );
}

function BundleTab({ runId, push, refresh }: { runId: string; push: (s: string) => void; refresh: () => Promise<void> }) {
    const submit = async (bundle: CheatBundle) => {
        try {
            const count = await cheatBundle(runId, bundle);
            await refresh();
            push(`번들 지급 [${bundle}]: ${count}개 아이템`);
        } catch (e: any) {
            push(`실패: ${e.message ?? e}`);
        }
    };
    return (
        <div style={tabInnerStyle}>
            <p style={{ fontSize: 12, opacity: 0.8 }}>
                프리셋 재료 번들 지급 — 한 번에 뭉텅이로.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <button style={bundleBtn} onClick={() => submit("refine")}>
                    <div style={{ fontWeight: "bold" }}>🔨 제련 세트</div>
                    <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
                        제련석×70, 장인의 재×20,<br/>연민×3, 수리석×5
                    </div>
                </button>
                <button style={bundleBtn} onClick={() => submit("engrave")}>
                    <div style={{ fontWeight: "bold" }}>✦ 각인 세트</div>
                    <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
                        11종×{"{"}std5 + ref3 + cry1{"}"} + 보조 3종
                    </div>
                </button>
                <button style={bundleBtn} onClick={() => submit("all")}>
                    <div style={{ fontWeight: "bold" }}>🎁 전체</div>
                    <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
                        제련 + 각인 합본
                    </div>
                </button>
            </div>
        </div>
    );
}

function TrainingTab({ runId, push, refresh }: { runId: string; push: (s: string) => void; refresh: () => Promise<void> }) {
    const [pathId, setPathId] = useState("seo.gyeoldan");
    const [value, setValue] = useState(24);
    const PATHS = [
        "seo.gyeoldan", "seo.seomgwang", "seo.suho", "seo.chugyeok", "seo.bulmyeol", "seo.seungbu",
        "hon.hwayeom", "hon.suryu", "hon.ilwol", "hon.hyangyuk", "hon.pungmu",
    ];
    const submit = async () => {
        try {
            const progress = await cheatSetTraining(runId, pathId, value);
            push(`훈련 세팅: ${pathId} = ${value} (전체 ${Object.keys(progress).length}개 path)`);
            await refresh();
        } catch (e: any) {
            push(`실패: ${e.message ?? e}`);
        }
    };
    return (
        <div style={tabInnerStyle}>
            <label style={labelStyle}>훈련 경로 (pathId)</label>
            <select
                value={pathId}
                onChange={e => setPathId(e.target.value)}
                style={inputStyle}
            >
                {PATHS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <label style={labelStyle}>누적 회수 (0~100, 24 = 마스터리 해금)</label>
            <input
                type="number"
                value={value}
                onChange={e => setValue(Number(e.target.value))}
                min={0}
                max={100}
                style={inputStyle}
            />
            <div style={quickRow}>
                {[0, 3, 8, 15, 24, 50].map(v => (
                    <button key={v} onClick={() => setValue(v)} style={quickBtn}>
                        {v} {v === 24 ? "(Lv5)" : ""}
                    </button>
                ))}
            </div>
            <PrimaryButton onClick={submit}>적용</PrimaryButton>
        </div>
    );
}

function EnhanceTab({ runId, push, refresh }: { runId: string; push: (s: string) => void; refresh: () => Promise<void> }) {
    const [instanceId, setInstanceId] = useState("");
    const [value, setValue] = useState(10);
    const submit = async () => {
        if (!instanceId) return;
        try {
            await cheatSetEnhancement(runId, instanceId, value);
            push(`제련 세팅: ${instanceId.slice(0, 8)}... = +${value}`);
            await refresh();
        } catch (e: any) {
            push(`실패: ${e.message ?? e}`);
        }
    };
    return (
        <div style={tabInnerStyle}>
            <label style={labelStyle}>장비 인스턴스 ID</label>
            <p style={{ fontSize: 11, opacity: 0.6, marginTop: -6, marginBottom: 8 }}>
                캐릭터 시트 → 장비 상세 카드에서 instanceId 확인 (또는 인벤토리 API 응답).
            </p>
            <input
                type="text"
                value={instanceId}
                onChange={e => setInstanceId(e.target.value)}
                placeholder="UUID"
                style={{ ...inputStyle, fontFamily: "monospace", fontSize: 11 }}
            />
            <label style={labelStyle}>제련 단계 (0~10)</label>
            <input
                type="number"
                value={value}
                onChange={e => setValue(Number(e.target.value))}
                min={0}
                max={10}
                style={inputStyle}
            />
            <div style={quickRow}>
                {[0, 4, 7, 8, 9, 10].map(v => (
                    <button key={v} onClick={() => setValue(v)} style={quickBtn}>+{v}</button>
                ))}
            </div>
            <PrimaryButton onClick={submit} disabled={!instanceId}>적용</PrimaryButton>
        </div>
    );
}

// ─── Styles ──────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 5000,
    display: "flex", alignItems: "center", justifyContent: "center",
};
const modalStyle: React.CSSProperties = {
    background: "#14101C", color: tokens.color.parchment,
    padding: 20, borderRadius: 10, width: 720, maxHeight: "90vh",
    border: `2px solid #FF6B6B`, boxShadow: "0 0 40px rgba(255,107,107,0.3)",
    display: "flex", flexDirection: "column", gap: 12,
    fontFamily: "monospace",
};
const headerStyle: React.CSSProperties = {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
};
const closeBtn: React.CSSProperties = {
    padding: "4px 10px", fontSize: 14, background: "transparent",
    color: tokens.color.parchment, border: `1px solid ${tokens.color.brass}55`,
    borderRadius: 4, cursor: "pointer",
};
const tabRow: React.CSSProperties = {
    display: "flex", gap: 4, borderBottom: `1px solid ${tokens.color.brass}22`, paddingBottom: 6,
};
const tabBtn: React.CSSProperties = {
    padding: "6px 12px", fontSize: 12,
    background: "transparent", border: `1px solid`,
    borderRadius: 4, cursor: "pointer", letterSpacing: 1,
};
const bodyStyle: React.CSSProperties = {
    flex: 1, overflowY: "auto",
};
const tabInnerStyle: React.CSSProperties = {
    display: "flex", flexDirection: "column", gap: 10, padding: 8,
};
const labelStyle: React.CSSProperties = {
    fontSize: 12, opacity: 0.8, marginTop: 8, marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
    padding: "8px 10px", fontSize: 13,
    background: "#1A1624", color: tokens.color.parchment,
    border: `1px solid ${tokens.color.brass}55`, borderRadius: 4,
    fontFamily: "inherit",
};
const quickRow: React.CSSProperties = {
    display: "flex", gap: 4, flexWrap: "wrap",
};
const quickBtn: React.CSSProperties = {
    padding: "4px 10px", fontSize: 11,
    background: "transparent", color: tokens.color.brass,
    border: `1px dashed ${tokens.color.brass}44`, borderRadius: 4, cursor: "pointer",
};
const listStyle: React.CSSProperties = {
    maxHeight: 240, overflowY: "auto",
    border: `1px solid ${tokens.color.brass}22`, borderRadius: 4,
    background: "#0F0B15",
};
const listRow: React.CSSProperties = {
    display: "flex", gap: 8, alignItems: "center",
    padding: "6px 10px", fontSize: 12,
    background: "transparent", color: tokens.color.parchment,
    border: "none", borderBottom: `1px solid ${tokens.color.brass}11`,
    cursor: "pointer", width: "100%", textAlign: "left",
};
const bundleBtn: React.CSSProperties = {
    padding: 16, fontSize: 13,
    background: `${tokens.color.brass}11`, color: tokens.color.parchment,
    border: `1px solid ${tokens.color.brass}55`, borderRadius: 6, cursor: "pointer",
    textAlign: "left",
};
const codeStyle: React.CSSProperties = {
    fontFamily: "monospace", fontSize: 11,
    background: "#0F0B15", padding: "2px 6px", borderRadius: 3,
    border: `1px solid ${tokens.color.brass}33`, color: tokens.color.brass,
};
const logStyle: React.CSSProperties = {
    maxHeight: 120, overflowY: "auto",
    background: "#0A0610", borderTop: `1px solid ${tokens.color.brass}22`,
    padding: 8, fontSize: 10, opacity: 0.9,
};
const logLine: React.CSSProperties = {
    fontFamily: "monospace",
    color: tokens.color.moss, lineHeight: 1.5,
};
